/**
 * Order Products Sync - Pub/Sub Event System
 *
 * A lightweight publish-subscribe utility that notifies subscribers when order products
 * are added, updated, or removed. This enables reactive updates across components that
 * depend on the current order's product selection.
 *
 * ## Purpose
 * When a user selects or deselects products in an order, the available themes need to
 * be re-fetched. The backend filters themes based on the application type (MDL/M3)
 * determined by the products in the order:
 * - If all products are M3 → only M3 themes are shown
 * - If all products are MDL → only MDL themes are shown
 * - If mixed → all themes are shown
 *
 * This utility decouples the product selection logic from theme fetching, allowing
 * any component to react to product changes without tight coupling.
 *
 * ## Technical Context
 * This is a workaround because Recoil selector setters cannot await async operations.
 * It signals to PrintPackageNav that the database write has finished and themes should
 * be refetched. Keep this scoped to just this use case — if similar behavior is needed
 * elsewhere, prefer using `useRecoilCallback` instead.
 *
 * Note: This utility will be replaced when we migrate to React Query.
 */

// `products`: fresh rows to hydrate subscribers with directly.
// `staleOrderId`: the backend flagged this order's client data as stale (theme
// self-heal — X-Order-Data-Stale response header); subscribers owning that
// order must refetch it from the backend, the source of truth.
type SyncEvent = { products?: unknown[]; staleOrderId?: string }
type Listener = (data?: SyncEvent) => void

const listeners: Set<Listener> = new Set()

export const orderProductsSync = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },

  notify(data?: SyncEvent): void {
    listeners.forEach((listener) => listener(data))
  },
}
